import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useLayoutEffect,
} from "react";
import { ethers } from "ethers";
import axios from "axios";
import Odometer from "odometer";
import "./DepositScreen.css";
import { useTranslation } from "../localization";

const resolveApiBaseUrl = () => {
  if (typeof window === "undefined") {
    return process.env.REACT_APP_API_URL || "http://localhost:3000/api";
  }

  const host = window.location.hostname;

  if (host === "bidarenas.com" || host === "www.bidarenas.com") {
    return "https://api.bidarenas.com/api";
  }

  if (host === "localhost" || host === "127.0.0.1") {
    return process.env.REACT_APP_API_URL || "http://localhost:3000/api";
  }

  return process.env.REACT_APP_API_URL || `${window.location.origin}/api`;
};

const API_BASE_URL = resolveApiBaseUrl();
const WALLET_PRESETS = [1, 5, 10, 20];

const OdometerValue = ({ value, format = "(,ddd).dd", duration = 900 }) => {
  const elementRef = useRef(null);
  const odometerRef = useRef(null);
  const previousDigitCountRef = useRef(null);

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const numericValue = Number(value ?? 0);
    const nextValue = Number.isFinite(numericValue) ? numericValue : 0;
    const digitCount = Math.trunc(Math.abs(nextValue)).toString().length;
    const lostDigit =
      previousDigitCountRef.current !== null &&
      digitCount < previousDigitCountRef.current;

    if (!odometerRef.current) {
      odometerRef.current = new Odometer({
        el: element,
        value: nextValue,
        format,
        duration,
        theme: "default",
      });
    } else if (lostDigit) {
      odometerRef.current.value = nextValue;
      odometerRef.current.render();
    } else {
      odometerRef.current.update(nextValue);
    }

    previousDigitCountRef.current = digitCount;
  }, [value, format, duration]);

  return <span ref={elementRef} />;
};

function WalletScreen({
  walletAddress,
  authToken,
  onChainBalance,
  platformBalance,
  withdrawableBalance,
  promotionalBalance,
  promotionClaimed,
  provider,
  onBackToGame,
  onBalanceUpdate,
  onRefreshOnChainBalance,
  onConnectWallet,
  onDisconnectWallet,
  amount,
  onAmountChange,
  externalMode,
  actionRequest,
  onActionHandled,
  infoRequest,
  onInfoHandled,
  dismissInfoRequest,
  onDismissInfoHandled,
  onInfoVisibilityChange,
  geoRestricted = false,
}) {
  const { language, t } = useTranslation();
  const [mode, setMode] = useState(
    withdrawableBalance >= 1 ? "withdraw" : "deposit",
  ); // "deposit" or "withdraw"
  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState(""); // 'success', 'error', 'info'
  const [pendingWithdrawals, setPendingWithdrawals] = useState([]);
  const [pendingDeposits, setPendingDeposits] = useState([]);
  const [showImportantInfo, setShowImportantInfo] = useState(false);
  const [localDeposit, setLocalDeposit] = useState(null);
  const [depositInfo, setDepositInfo] = useState(null);
  const [lastDepositStatus, setLastDepositStatus] = useState(null);
  const [statusCardIndex, setStatusCardIndex] = useState(0);
  const [errorMessageReceivedAt, setErrorMessageReceivedAt] = useState(0);
  const [claimAfterConnect, setClaimAfterConnect] = useState(false);
  const [claimingPromotion, setClaimingPromotion] = useState(false);
  const handledActionRef = useRef(null);
  const handledInfoRef = useRef(null);
  const handledDismissRef = useRef(null);
  const previousTopStatusCardKeyRef = useRef("");
  const popupBodyRef = useRef(null);
  const claimInFlightRef = useRef(false);

  useEffect(() => {
    if (!externalMode) {
      return undefined;
    }

    setMode((previousMode) =>
      previousMode === externalMode ? previousMode : externalMode,
    );

    return undefined;
  }, [externalMode]);

  useEffect(() => {
    let isMounted = true;

    const fetchDepositInfo = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/deposits/address`);
        if (isMounted) {
          setDepositInfo(response.data);
        }
      } catch (error) {
        console.error("Error fetching deposit info:", error);
      }
    };

    fetchDepositInfo();

    return () => {
      isMounted = false;
    };
  }, []);

  const fetchPendingWithdrawals = useCallback(async () => {
    if (!walletAddress || !authToken) {
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/withdrawals/pending`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setPendingWithdrawals(response.data.pending || []);
    } catch (error) {
      console.error("Error fetching pending withdrawals:", error);
    }
  }, [authToken, walletAddress]);

  const fetchPendingDeposits = useCallback(async () => {
    if (!walletAddress || !authToken) {
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/deposits/pending`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setPendingDeposits(response.data.pending || []);
    } catch (error) {
      console.error("Error fetching pending deposits:", error);
    }
  }, [authToken, walletAddress]);

  const fetchLatestDepositStatus = useCallback(async () => {
    if (!walletAddress || !authToken || !lastDepositStatus?.txHash) {
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/deposits/history`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      const match = (response.data.deposits || []).find(
        (deposit) => deposit.txHash === lastDepositStatus.txHash,
      );

      if (match) {
        setLastDepositStatus((prev) => {
          if (!prev) {
            return { ...match };
          }

          const unchanged =
            prev.status === match.status &&
            prev.confirmations === match.confirmations &&
            prev.updatedAt === match.updatedAt &&
            prev.completedAt === match.completedAt;

          if (unchanged) {
            return prev;
          }

          return {
            ...prev,
            ...match,
          };
        });
      } else if (localDeposit?.txHash === lastDepositStatus.txHash) {
        const validateResponse = await axios.post(
          `${API_BASE_URL}/deposits/validate`,
          { txHash: localDeposit.txHash },
          { headers: { Authorization: "Bearer " + authToken } },
        );

        if (validateResponse.data?.valid) {
          await fetchPendingDeposits();
        }
      }
    } catch (error) {
      console.error("Error fetching latest deposit status:", error);
    }
  }, [
    authToken,
    fetchPendingDeposits,
    lastDepositStatus?.txHash,
    localDeposit?.txHash,
    walletAddress,
  ]);

  useEffect(() => {
    if (mode === "withdraw" && walletAddress) {
      fetchPendingWithdrawals();
      const interval = setInterval(() => {
        fetchPendingWithdrawals();
      }, 10000);
      return () => clearInterval(interval);
    }
    if (mode === "deposit" && walletAddress) {
      fetchPendingDeposits();
      const interval = setInterval(() => {
        fetchPendingDeposits();
        fetchLatestDepositStatus();
      }, 10000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [
    mode,
    walletAddress,
    fetchPendingWithdrawals,
    fetchPendingDeposits,
    fetchLatestDepositStatus,
  ]);

  useEffect(() => {
    if (!localDeposit) {
      return undefined;
    }

    const matchingDeposit = pendingDeposits.find(
      (deposit) => deposit.txHash && deposit.txHash === localDeposit.txHash,
    );

    if (matchingDeposit) {
      setLocalDeposit(null);
    }

    return undefined;
  }, [localDeposit, pendingDeposits]);

  useEffect(() => {
    if (localDeposit) {
      setLastDepositStatus((previousStatus) => {
        if (
          previousStatus?.txHash === localDeposit.txHash &&
          previousStatus?.status === localDeposit.status &&
          previousStatus?.confirmations === localDeposit.confirmations &&
          previousStatus?.amount === localDeposit.amount
        ) {
          return previousStatus;
        }

        return { ...localDeposit };
      });
      return;
    }

    if (pendingDeposits.length > 0) {
      const latestPendingDeposit = pendingDeposits[0];

      setLastDepositStatus((previousStatus) => {
        if (
          previousStatus?.id === latestPendingDeposit?.id &&
          previousStatus?.txHash === latestPendingDeposit?.txHash &&
          previousStatus?.status === latestPendingDeposit?.status &&
          previousStatus?.confirmations === latestPendingDeposit?.confirmations
        ) {
          return previousStatus;
        }

        return { ...latestPendingDeposit };
      });
      return;
    }

    if (lastDepositStatus && lastDepositStatus.status !== "confirmed") {
      fetchLatestDepositStatus();
    }
  }, [
    localDeposit,
    pendingDeposits,
    lastDepositStatus,
    fetchLatestDepositStatus,
  ]);

  const depositItems = localDeposit
    ? [
        localDeposit,
        ...pendingDeposits.filter(
          (deposit) => deposit.txHash !== localDeposit.txHash,
        ),
      ]
    : pendingDeposits;

  const handleDeposit = useCallback(async () => {
    setStatusMessage("");
    setStatusType("");
    setLastDepositStatus(null);

    if (!walletAddress) {
      setStatusMessage(t("wallet.connectFirst"));
      setStatusType("error");
      return;
    }

    if (!provider) {
      setStatusMessage(t("wallet.providerUnavailable"));
      setStatusType("error");
      return;
    }

    if (!depositInfo?.address || !depositInfo?.tokenAddress) {
      setStatusMessage(t("wallet.depositUnavailable"));
      setStatusType("error");
      return;
    }

    let refreshedOnChainBalance = onChainBalance;
    if (onRefreshOnChainBalance) {
      const latestBalance = await onRefreshOnChainBalance();
      if (typeof latestBalance === "number" && !Number.isNaN(latestBalance)) {
        refreshedOnChainBalance = latestBalance;
      }
    }

    const amountValue = parseFloat(amount || "0");
    if (isNaN(amountValue) || amountValue <= 0) {
      setStatusMessage(t("wallet.validAmountPositive"));
      setStatusType("error");
      return;
    }

    if (amountValue > refreshedOnChainBalance) {
      setStatusMessage(t("wallet.insufficientWallet"));
      setStatusType("error");
      return;
    }

    try {
      const signer = provider.getSigner();
      const usdcAddress = depositInfo.tokenAddress;
      const platformAddress = depositInfo.address;
      const usdcAbi = [
        "function transfer(address to, uint256 amount) returns (bool)",
      ];
      const usdcContract = new ethers.Contract(usdcAddress, usdcAbi, signer);
      const amountInWei = ethers.utils.parseUnits(amount, 6);

      const tx = await usdcContract.populateTransaction.transfer(
        platformAddress,
        amountInWei,
      );

      const txHash = await window.ethereum.request({
        method: "eth_sendTransaction",
        params: [
          {
            from: walletAddress,
            to: usdcAddress,
            data: tx.data,
            gas: ethers.utils.hexlify(100000),
          },
        ],
      });

      setStatusMessage("");
      setStatusType("");
      setLocalDeposit({
        amount: amountValue,
        status: "pending",
        confirmations: 0,
        createdAt: new Date().toISOString(),
        txHash,
      });
      onAmountChange?.("1.00");
      fetchPendingDeposits();

      if (authToken) {
        try {
          const validateResponse = await axios.post(
            `${API_BASE_URL}/deposits/validate`,
            { txHash },
            {
              headers: {
                Authorization: `Bearer ${authToken}`,
              },
            },
          );

          if (validateResponse.data?.valid === false) {
            setStatusMessage(t("wallet.depositMismatch"));
            setStatusType("error");
          } else {
            await fetchPendingDeposits();
          }
        } catch (validateError) {
          console.error("Deposit validation failed:", validateError);
        }
      }

      provider
        .waitForTransaction(txHash)
        .then((receipt) => {
          onBalanceUpdate();
        })
        .catch((receiptError) => {
          setTimeout(onBalanceUpdate, 3000);
        });
    } catch (error) {
      if (
        error.code === 4001 ||
        error.message?.includes("User rejected") ||
        error.message?.includes("denied")
      ) {
        setStatusMessage(t("wallet.cancelled"));
        setStatusType("info");
      } else if (error.message?.includes("invalid BigNumber")) {
        setStatusMessage(t("wallet.parseError"));
        setStatusType("info");
      } else {
        const errorMsg = error.reason || error.message || "Unknown error";
        setStatusMessage(t("wallet.depositFailed", { error: errorMsg }));
        setStatusType("error");
      }
    } finally {
    }
  }, [
    amount,
    authToken,
    depositInfo,
    fetchPendingDeposits,
    onAmountChange,
    onBalanceUpdate,
    onChainBalance,
    onRefreshOnChainBalance,
    provider,
    t,
    walletAddress,
  ]);

  const handleWithdraw = useCallback(async () => {
    setLastDepositStatus(null);
    if (!walletAddress) {
      setStatusMessage(t("wallet.connectFirst"));
      setStatusType("error");
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      setStatusMessage(t("wallet.validAmount"));
      setStatusType("error");
      return;
    }

    const amountValue = parseFloat(amount || "0");
    if (amountValue > withdrawableBalance) {
      setStatusMessage(t("wallet.insufficientPlatform"));
      setStatusType("error");
      return;
    }

    setStatusMessage("");

    try {
      await axios.post(
        `${API_BASE_URL}/withdrawals`,
        {
          amount: amountValue,
          toAddress: walletAddress,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      setStatusMessage("");
      setStatusType("");
      onAmountChange?.("1.00");
      await onBalanceUpdate();
      fetchPendingWithdrawals();
    } catch (error) {
      console.error("Withdrawal error:", error);
      setStatusMessage(
        error.response?.data?.error ||
          error.message ||
          t("wallet.withdrawalFailed"),
      );
      setStatusType("error");
    } finally {
    }
  }, [
    amount,
    authToken,
    fetchPendingWithdrawals,
    onAmountChange,
    onBalanceUpdate,
    t,
    walletAddress,
    withdrawableBalance,
  ]);

  const handleClaimPromotion = useCallback(async () => {
    if (promotionClaimed || claimInFlightRef.current) {
      return;
    }

    if (!walletAddress || !authToken) {
      setClaimAfterConnect(true);
      onConnectWallet?.();
      return;
    }

    claimInFlightRef.current = true;
    setClaimingPromotion(true);
    setStatusMessage("");
    setStatusType("");

    try {
      await axios.post(
        `${API_BASE_URL}/wallet/promotion/claim`,
        {},
        {
          headers: {
            Authorization: "Bearer " + authToken,
          },
        },
      );
      await onBalanceUpdate?.();
    } catch (error) {
      if (error.response?.status === 409) {
        await onBalanceUpdate?.();
      } else {
        setStatusMessage(
          error.response?.data?.error || t("wallet.claimFailed"),
        );
        setStatusType("error");
      }
    } finally {
      claimInFlightRef.current = false;
      setClaimingPromotion(false);
      setClaimAfterConnect(false);
    }
  }, [
    authToken,
    onBalanceUpdate,
    onConnectWallet,
    promotionClaimed,
    t,
    walletAddress,
  ]);

  useEffect(() => {
    if (claimAfterConnect && walletAddress && authToken) {
      handleClaimPromotion();
    }
  }, [authToken, claimAfterConnect, handleClaimPromotion, walletAddress]);

  useEffect(() => {
    if (!actionRequest) {
      return undefined;
    }

    if (handledActionRef.current === actionRequest.requestId) {
      return undefined;
    }

    handledActionRef.current = actionRequest.requestId;

    if (actionRequest.type === "deposit") {
      setMode("deposit");
      setStatusMessage("");
      Promise.resolve(handleDeposit()).finally(() => {
        onActionHandled?.(actionRequest.requestId);
      });
      return undefined;
    }

    if (actionRequest.type === "withdraw") {
      setMode("withdraw");
      setStatusMessage("");
      Promise.resolve(handleWithdraw()).finally(() => {
        onActionHandled?.(actionRequest.requestId);
      });
    }

    return undefined;
  }, [actionRequest, handleDeposit, handleWithdraw, onActionHandled]);

  useEffect(() => {
    if (!infoRequest) {
      return undefined;
    }

    if (handledInfoRef.current === infoRequest.requestId) {
      return undefined;
    }

    handledInfoRef.current = infoRequest.requestId;

    setShowImportantInfo(true);

    onInfoHandled?.(infoRequest.requestId);
    return undefined;
  }, [infoRequest, onInfoHandled]);

  useEffect(() => {
    if (!dismissInfoRequest) {
      return undefined;
    }

    if (handledDismissRef.current === dismissInfoRequest.requestId) {
      return undefined;
    }

    handledDismissRef.current = dismissInfoRequest.requestId;

    setShowImportantInfo(false);
    onDismissInfoHandled?.(dismissInfoRequest.requestId);
    return undefined;
  }, [dismissInfoRequest, onDismissInfoHandled]);

  useEffect(() => {
    onInfoVisibilityChange?.(showImportantInfo);
  }, [onInfoVisibilityChange, showImportantInfo]);

  useLayoutEffect(() => {
    const popupBody = popupBodyRef.current;
    if (!showImportantInfo || !popupBody) {
      return undefined;
    }

    const bottomActions = document.querySelector(".game-bottom-actions");
    if (!bottomActions) {
      return undefined;
    }

    const updateFadePosition = () => {
      const popupRect = popupBody.getBoundingClientRect();
      const actionsRect = bottomActions.getBoundingClientRect();
      const fadeEnd = Math.max(1, actionsRect.top - popupRect.top - 16);
      const fadeStart = Math.max(0, fadeEnd - actionsRect.height);

      popupBody.style.setProperty(
        "--wallet-popup-fade-start",
        `${fadeStart}px`,
      );
      popupBody.style.setProperty("--wallet-popup-fade-end", `${fadeEnd}px`);
      popupBody.style.setProperty(
        "--wallet-popup-scroll-height",
        `${fadeEnd}px`,
      );
    };

    const frameId = window.requestAnimationFrame(updateFadePosition);
    const resizeObserver = new ResizeObserver(updateFadePosition);
    resizeObserver.observe(popupBody);
    resizeObserver.observe(bottomActions);
    window.addEventListener("resize", updateFadePosition);

    return () => {
      window.cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateFadePosition);
    };
  }, [showImportantInfo]);

  useEffect(() => {
    if (statusMessage && statusType === "error") {
      setErrorMessageReceivedAt(Date.now());
    }
  }, [statusMessage, statusType]);

  const abbreviatedWalletAddress =
    walletAddress && walletAddress.length > 10
      ? `${walletAddress.substring(0, 6)}...${walletAddress.substring(walletAddress.length - 4)}`
      : walletAddress;
  const selectedAmount = Number.parseFloat(amount || "0");
  const maxSelectableAmount = Math.max(
    0,
    mode === "withdraw" ? withdrawableBalance : onChainBalance,
  );
  const selectedAmountDisplay = Number.isFinite(selectedAmount)
    ? selectedAmount.toFixed(2)
    : "0.00";
  const primaryDeposit =
    (depositItems.length > 0 ? depositItems[0] : lastDepositStatus) || null;
  const primaryWithdrawal =
    pendingWithdrawals.length > 0 ? pendingWithdrawals[0] : null;

  const formatStatusAmount = (value) => {
    const numericValue = parseFloat(value);
    return Number.isFinite(numericValue)
      ? `$${numericValue.toFixed(2)} USDC`
      : "USDC";
  };

  const formatStatusDate = (value) =>
    value
      ? new Date(value).toLocaleString(
          language === "fr" ? "fr-FR" : language === "es" ? "es-ES" : "en-US",
        )
      : "";
  const getDepositRequiredConfirmations = () =>
    Number.isFinite(Number(depositInfo?.confirmationsRequired))
      ? Number(depositInfo.confirmationsRequired)
      : 64;
  const getDepositConfirmationCount = (deposit) =>
    Number.isFinite(Number(deposit?.confirmations))
      ? Number(deposit.confirmations)
      : 0;
  const getDepositCompletedTimestamp = (deposit) =>
    deposit?.completedAt ||
    deposit?.processedAt ||
    deposit?.updatedAt ||
    deposit?.completed_at ||
    null;
  const getCompletedTimestamp = (withdrawal) =>
    withdrawal?.completedAt ||
    withdrawal?.processedAt ||
    withdrawal?.updatedAt ||
    withdrawal?.completed_at ||
    null;

  const toTimestamp = (value) => {
    if (!value) {
      return 0;
    }

    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const statusCards = useMemo(() => {
    const cards = [];

    if (primaryDeposit) {
      const depositTimestamp = Math.max(
        toTimestamp(primaryDeposit.completedAt),
        toTimestamp(primaryDeposit.processedAt),
        toTimestamp(primaryDeposit.updatedAt),
        toTimestamp(primaryDeposit.completed_at),
        toTimestamp(primaryDeposit.createdAt),
        toTimestamp(primaryDeposit.requestedAt),
      );

      cards.push({
        key: `deposit-${primaryDeposit.txHash || primaryDeposit.id || "latest"}-${depositTimestamp}`,
        type: "deposit",
        timestamp: depositTimestamp,
        data: primaryDeposit,
      });
    }

    if (primaryWithdrawal) {
      const withdrawalTimestamp = Math.max(
        toTimestamp(primaryWithdrawal.completedAt),
        toTimestamp(primaryWithdrawal.processedAt),
        toTimestamp(primaryWithdrawal.updatedAt),
        toTimestamp(primaryWithdrawal.completed_at),
        toTimestamp(primaryWithdrawal.requestedAt),
        toTimestamp(primaryWithdrawal.createdAt),
      );

      cards.push({
        key: `withdrawal-${primaryWithdrawal.txHash || primaryWithdrawal.id || "latest"}-${withdrawalTimestamp}`,
        type: "withdrawal",
        timestamp: withdrawalTimestamp,
        data: primaryWithdrawal,
      });
    }

    if (statusMessage && statusType === "error") {
      cards.push({
        key: `error-${errorMessageReceivedAt}-${statusMessage}`,
        type: "error",
        timestamp: errorMessageReceivedAt || Date.now(),
        message: statusMessage,
      });
    }

    cards.sort((leftCard, rightCard) => rightCard.timestamp - leftCard.timestamp);
    return cards;
  }, [
    errorMessageReceivedAt,
    primaryDeposit,
    primaryWithdrawal,
    statusMessage,
    statusType,
  ]);

  useEffect(() => {
    if (statusCards.length === 0) {
      if (statusCardIndex !== 0) {
        setStatusCardIndex(0);
      }
      previousTopStatusCardKeyRef.current = "";
      return;
    }

    const currentTopKey = statusCards[0].key;
    if (
      currentTopKey &&
      previousTopStatusCardKeyRef.current !== currentTopKey
    ) {
      previousTopStatusCardKeyRef.current = currentTopKey;
      if (statusCardIndex !== 0) {
        setStatusCardIndex(0);
      }
      return;
    }

    if (statusCardIndex > statusCards.length - 1) {
      setStatusCardIndex(statusCards.length - 1);
    }
  }, [statusCardIndex, statusCards]);

  const activeStatusCard =
    statusCards.length > 0
      ? statusCards[Math.min(statusCardIndex, statusCards.length - 1)]
      : null;

  return (
    <div className="deposit-screen wallet-screen-popup-host">
      <div className="info-card">
        <div className="wallet-balance-grid">
          <div className="balance-row wallet-balance-card">
            <span
              className={`balance-label${abbreviatedWalletAddress ? " wallet-address-label" : ""}`}
            >
              {abbreviatedWalletAddress || <>{t("wallet.connectedBalance")}</>}
            </span>
            <span className="balance-value money-value">
              <span className="money-prefix wallet-money-prefix">$</span>
              <OdometerValue
                value={
                  Number.isFinite(Number(onChainBalance))
                    ? Number(onChainBalance)
                    : 0
                }
                format="(,ddd).dd"
              />
            </span>
          </div>
          <div className="balance-row amount-display-row">
            <span className="balance-label amount-display-label">
              {t("wallet.depositWithdrawal")}
            </span>
            <span className="balance-value money-value amount-selected-value">
              <span className="money-prefix wallet-money-prefix">$</span>
              <span className="money-amount">{selectedAmountDisplay}</span>
            </span>
          </div>
          {promotionalBalance > 0 && (
            <div className="balance-row wallet-balance-card">
              <span className="balance-label">
                {t("wallet.promotionalBalance")}
              </span>
              <span className="balance-value money-value">
                <span className="money-prefix wallet-money-prefix">$</span>
                <span className="money-amount">
                  {Number(promotionalBalance).toFixed(2)}
                </span>
              </span>
            </div>
          )}
          <div className="balance-row wallet-balance-card">
            <span className="balance-label">{t("wallet.bidArenasBalance")}</span>
            <span className="balance-value money-value">
              <span className="money-prefix wallet-money-prefix">$</span>
              <span className="money-amount">
                {Number.isFinite(Number(platformBalance))
                  ? Number(platformBalance).toFixed(2)
                  : "0.00"}
              </span>
            </span>
          </div>
        </div>
        <>
          <div className="balance-row wallet-connect-row">
            <div className="amount-button-row">
              {WALLET_PRESETS.map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`amount-button${
                    Math.abs(parseFloat(amount || "0") - value) < 0.001
                      ? " amount-button-selected"
                      : ""
                  }`}
                  onClick={() => onAmountChange?.(value.toFixed(2))}
                >
                  ${value}
                </button>
              ))}
              <button
                type="button"
                className={`amount-button${
                  Math.abs(parseFloat(amount || "0") - maxSelectableAmount) <
                  0.001
                    ? " amount-button-selected"
                    : ""
                }`}
                onClick={() =>
                  onAmountChange?.(Math.max(0, maxSelectableAmount).toFixed(2))
                }
              >
                ${t("wallet.all")}
              </button>
            </div>
          </div>
          <div className="balance-row wallet-action-row">
            <button
              type="button"
              className="wallet-action-button"
              onClick={() => setShowImportantInfo(true)}
            >
              {t("wallet.readMe")}
            </button>
            {!promotionClaimed && (
              <button
                type="button"
                className="wallet-action-button"
                onClick={handleClaimPromotion}
                disabled={claimingPromotion}
              >
                {claimingPromotion
                  ? t("wallet.claiming")
                  : t("wallet.claimCredit")}
              </button>
            )}
            <button
              type="button"
              className="wallet-action-button"
              onClick={() => {
                setMode("deposit");
                handleDeposit();
              }}
            >
              {t("wallet.deposit")}
            </button>
            <button
              type="button"
              className="wallet-action-button"
              onClick={() => {
                setMode("withdraw");
                handleWithdraw();
              }}
            >
              {t("wallet.withdraw")}
            </button>
          </div>
          {!walletAddress && (
            <p className="wallet-promotion-connect-message">
              {t("wallet.promotionConnectMessage")}
            </p>
          )}
        </>
      </div>

      {activeStatusCard && (
        <>
          <div className="wallet-status-block">
            <div className="wallet-status-title wallet-status-message">
              {activeStatusCard.type === "deposit" &&
                (() => {
                  const deposit = activeStatusCard.data;
                  const requestedAt = deposit.createdAt || deposit.requestedAt;
                  const completedAt = getDepositCompletedTimestamp(deposit);
                  const requiredConfirmations = getDepositRequiredConfirmations();
                  const currentConfirmations =
                    getDepositConfirmationCount(deposit);
                  const hasSufficientConfirmations =
                    deposit.status === "confirmed" ||
                    currentConfirmations >= requiredConfirmations;

                  return (
                    <>
                      {hasSufficientConfirmations && deposit.txHash && (
                        <a
                          href={`https://polygonscan.com/tx/${deposit.txHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="wallet-status-polygon-icon"
                          aria-label={t("wallet.viewPolygon")}
                          title={t("wallet.viewPolygon")}
                        >
                          <svg
                            className="wallet-status-polygon-icon-svg"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 178 161"
                            aria-hidden="true"
                            focusable="false"
                          >
                            <path
                              fill="#6C00F6"
                              d="M66.8,54.7l-16.7-9.7L0,74.1v58l50.1,29l50.1-29V41.9L128,25.8l27.8,16.1v32.2L128,90.2l-16.7-9.7v25.8l16.7,9.7l50.1-29V29L128,0L77.9,29v90.2l-27.8,16.1l-27.8-16.1V86.9l27.8-16.1l16.7,9.7V54.7z"
                            />
                          </svg>
                        </a>
                      )}
                      <span className="wallet-status-message-primary">
                        {`${formatStatusAmount(deposit.amount)} ${t("wallet.depositNoun")}`}
                      </span>
                      <span className="wallet-status-message-secondary">
                        <span className="wallet-status-time-line">
                          {t("wallet.requested", {
                            date: formatStatusDate(requestedAt) || "[timestamp]",
                          })}
                        </span>
                        {hasSufficientConfirmations ? (
                          <span className="wallet-status-time-line">
                            {t("wallet.completed", {
                              date:
                                formatStatusDate(completedAt) || "[timestamp]",
                            })}
                          </span>
                        ) : (
                          <span className="wallet-status-time-line">
                            {t("wallet.confirmations", {
                              current: currentConfirmations,
                              required: requiredConfirmations,
                            })}
                          </span>
                        )}
                      </span>
                    </>
                  );
                })()}

              {activeStatusCard.type === "withdrawal" &&
                (() => {
                  const withdrawal = activeStatusCard.data;
                  const requestedAt =
                    withdrawal.requestedAt || withdrawal.createdAt;
                  const completedAt = getCompletedTimestamp(withdrawal);
                  const isCompleted =
                    withdrawal.status === "completed" && Boolean(completedAt);

                  return (
                    <>
                      {withdrawal.txHash && (
                        <a
                          href={`https://polygonscan.com/tx/${withdrawal.txHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="wallet-status-polygon-icon"
                          aria-label={t("wallet.viewPolygon")}
                          title={t("wallet.viewPolygon")}
                        >
                          <svg
                            className="wallet-status-polygon-icon-svg"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 178 161"
                            aria-hidden="true"
                            focusable="false"
                          >
                            <path
                              fill="#6C00F6"
                              d="M66.8,54.7l-16.7-9.7L0,74.1v58l50.1,29l50.1-29V41.9L128,25.8l27.8,16.1v32.2L128,90.2l-16.7-9.7v25.8l16.7,9.7l50.1-29V29L128,0L77.9,29v90.2l-27.8,16.1l-27.8-16.1V86.9l27.8-16.1l16.7,9.7V54.7z"
                            />
                          </svg>
                        </a>
                      )}
                      <span className="wallet-status-message-primary">
                        {`${formatStatusAmount(withdrawal.amount)} ${t("wallet.withdrawalNoun")}`}
                      </span>
                      <span className="wallet-status-message-secondary">
                        <span className="wallet-status-time-line">
                          {t("wallet.requested", {
                            date: formatStatusDate(requestedAt) || "[timestamp]",
                          })}
                        </span>
                        <span className="wallet-status-completed-row">
                          <span className="wallet-status-time-line">
                            {isCompleted
                              ? t("wallet.completed", {
                                  date:
                                    formatStatusDate(completedAt) ||
                                    "[timestamp]",
                                })
                              : t("wallet.pending")}
                          </span>
                        </span>
                      </span>
                    </>
                  );
                })()}

              {activeStatusCard.type === "error" && (
                <>
                  <span className="wallet-status-message-secondary">
                    <span className="wallet-status-time-line">
                      {activeStatusCard.message}
                    </span>
                  </span>
                </>
              )}
            </div>
          </div>

          {statusCards.length > 1 && (
            <div
              className="wallet-status-nav"
              aria-label={t("wallet.statusNavigation")}
            >
              <button
                type="button"
                className="wallet-status-nav-button"
                onClick={() =>
                  setStatusCardIndex((previousIndex) =>
                    Math.min(statusCards.length - 1, previousIndex + 1),
                  )
                }
                disabled={statusCardIndex >= statusCards.length - 1}
                aria-label={t("wallet.olderStatus")}
              >
                <span className="wallet-status-nav-glyph">{"<"}</span>
              </button>
              <button
                type="button"
                className="wallet-status-nav-button"
                onClick={() =>
                  setStatusCardIndex((previousIndex) =>
                    Math.max(0, previousIndex - 1),
                  )
                }
                disabled={statusCardIndex === 0}
                aria-label={t("wallet.newerStatus")}
              >
                <span className="wallet-status-nav-glyph">{">"}</span>
              </button>
            </div>
          )}
        </>
      )}

      <div className="wallet-bottom-actions"></div>

      {showImportantInfo && (
        <div className="wallet-popup">
          <div className="wallet-popup-panel">
            <div ref={popupBodyRef} className="wallet-popup-body">
              {geoRestricted && (
                <>
                  <p className="warning-paragraph">
                    <span className="wallet-subheader">
                      {t("readMe.notice")}
                    </span>
                  </p>
                  <p className="warning-paragraph">
                    <strong>
                      {t("readMe.restricted")}
                    </strong>
                  </p>
                </>
              )}
              <p className="warning-paragraph">
                {t("readMe.freePlayPots")}
              </p>
              <p className="warning-paragraph">
                {t("readMe.promotionalCredit")}
              </p>
              <p className="warning-paragraph">
                <span className="wallet-subheader">{t("readMe.deposits")}</span>
              </p>
              <p className="warning-paragraph">
                {t("readMe.depositIntro")}
              </p>
              <p className="warning-paragraph">
                <span className="wallet-subheader">
                  {t("readMe.tokenAddress")}
                </span>
                <span className="wallet-highlight wallet-highlight-small">
                  {depositInfo?.tokenAddress || t("wallet.loading")}
                </span>
              </p>
              <p className="warning-paragraph">
                <span className="wallet-subheader">
                  {t("readMe.platformAddress")}
                </span>
                <span className="wallet-highlight wallet-highlight-small">
                  {depositInfo?.address || t("wallet.loading")}
                </span>
              </p>
              <p className="warning-paragraph">
                {t("readMe.usdcOnly")}
              </p>
              <p className="warning-paragraph">
                {t("readMe.depositTiming")}
              </p>
              <p className="warning-paragraph">
                <span className="wallet-subheader">
                  {t("readMe.withdrawals")}
                </span>
              </p>
              <p className="warning-paragraph">
                {t("readMe.withdrawalLimits")}
              </p>
              <p className="warning-paragraph">
                {t("readMe.gasFees")}
              </p>
              <p className="warning-paragraph">
                <span className="wallet-subheader">{t("readMe.security")}</span>
              </p>
              <p className="warning-paragraph">
                {t("readMe.privateKeys")}
              </p>
              <p className="warning-paragraph">
                {t("readMe.permissions")}
              </p>
              <p className="warning-paragraph">
                {t("readMe.signatures")}
              </p>
              <p className="warning-paragraph">
                {t("readMe.bestPractice")}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WalletScreen;
